import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type CardKinds = 'common' | 'memo' | 'ui' | 'draw';

export type CardTemplateDb = {
  name: string;
  kind: CardKinds;
  author: string;
  standard: boolean;
  defaultWidth: number;
  defaultHeight: number;
  frame: { display: boolean, color: string };
  background: { display: boolean, color: string };
  shadow: boolean;
  notes: string;
  isGroup: boolean;
  goodsId?: string;
  version?: number;
}
const initial: CardTemplateDb = {
  name: '',
  kind: 'common',
  author: '',
  standard: false,
  defaultWidth: 200,
  defaultHeight: 120,
  frame: { display: true, color: '#000000' },
  background: { display: true, color: '#ffffff' },
  shadow: true,
  notes: '',
  isGroup: false,
  goodsId: undefined,
  version: undefined
};

export type CardTemplateDbHashType = {
  [key in string]: CardTemplateDb;
}

export type CardTemplateAddPayload = {
  id?: string;
} & CardTemplateDb;
export type CardTemplateUpdatePayload = {
  id: string;
} & { [P in keyof CardTemplateDb]?: CardTemplateDb[P] };

export type CardTemplateDbState = {
  cardTemplates: CardTemplateDbHashType;
}

export const generateCardTemplateId = (): string => generateId('ct');
let initialState: CardTemplateDbState = { cardTemplates: {} };
const key = 'cardTemplates';

const cardTemplateDbSlice = createSlice({
  name: 'cardTemplateDb',
  initialState,
  reducers: {
    initializeCardTemplates(state: CardTemplateDbState) {
      state.cardTemplates = {};
    },
    addCardTemplate(state: CardTemplateDbState, action: PayloadAction<CardTemplateAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardTemplateId();
      state.cardTemplates[id] = obj;
      saveData(key, id, obj);
    },
    updateCardTemplate(state: CardTemplateDbState, action: PayloadAction<CardTemplateUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.cardTemplates[id] === undefined) {
        console.log(`*** CardTemplate not found: ${id}`);
      } else {
        const newData = { ...state.cardTemplates[id], ...obj };
        state.cardTemplates[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCardTemplate(state: CardTemplateDbState, action: PayloadAction<CardTemplateUpdatePayload>) {
      const id = action.payload.id;
      delete state.cardTemplates[id];
      deleteData(key, id);
    },
    saveCardTemplates(state: CardTemplateDbState, action: PayloadAction<void>) {
      saveAllData(key, state.cardTemplates);
    },
    signUpCardTemplates(state: CardTemplateDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cardTemplates, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardTemplateDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cardTemplates = updatedValues;
      if (changed) saveAllData(key, state.cardTemplates);
    })
  }
})

export const { initializeCardTemplates, addCardTemplate, updateCardTemplate, deleteCardTemplate, saveCardTemplates, signUpCardTemplates } = cardTemplateDbSlice.actions;
export default cardTemplateDbSlice.reducer;
